// src/utils/kpi.ts
// src/utils/kpi.ts
export type V3Row = {
  id: string;
  quote_currency?: string | null;
  bot_quote_currency?: string | null;
  net_profit?: number | null;
  net_profit_pct?: number | null;
  total_profit_pct?: number | null;
  total_profit_pct_weighted?: number | null;
  total_profit_amount?: number | null;
  total_profit_usd?: number | null;
  net_profit_amount?: number | null;
  total_cost_usd?: number | null;
  total_volume_usd?: number | null;
  fees_total_usd?: number | null;
  pairs_tested?: number | null;
  tested_pairs?: any[] | null;
  max_drawdown_pct?: number | null;
  max_drawdown?: number | null;
  account_balance?: number | null;
  initial_capital?: number | null;
  trade_history?: any[] | null;
  profit?: number | null;
  pnl_usd?: number | null;
  pairs_data?: any;
  pairs_stats_top10?: any;
};

const toNum = (v: any, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

export const formatPctFromFraction = (pct?: number | null, digits = 2) => {
  if (pct == null || !Number.isFinite(Number(pct))) return `0.00%`;
  return `${Number(pct).toFixed(digits)}%`;
};

// Išvalytas valiutos prirašymas, nuskaitomas tik iš formos kintamųjų
export const formatCurrency = (amount?: number | null, curr?: string | null, digits = 2) => {
  const n = toNum(amount, 0);
  const c = curr ? String(curr).toUpperCase() : ''; 
  return `${n.toFixed(digits)} ${c}`.trim();
};

export function computeCardKpis(row: V3Row, initialBalanceFromProps?: number) {
  let pairs: any[] = [];
  const rawPairs = (row as any)?.pairs_stats_top10 ?? (row as any)?.pairs_data ?? (row as any)?.pairsData ?? [];

  if (Array.isArray(rawPairs)) {
    pairs = rawPairs;
  } else if (typeof rawPairs === 'string' && rawPairs.startsWith('[')) {
    try { pairs = JSON.parse(rawPairs); } catch (e) { pairs = []; }
  } else if (rawPairs && typeof rawPairs === 'object' && Array.isArray(rawPairs.data)) {
    pairs = rawPairs.data;
  }

  const pairsCount = (typeof row?.pairs_tested === 'number' ? row.pairs_tested! : Array.isArray(row?.tested_pairs) ? row.tested_pairs!.length : 0) ?? 0;

  const capital = toNum(initialBalanceFromProps) || toNum(row?.account_balance) || toNum(row?.initial_capital) || 0;
  const quote = (row?.quote_currency || row?.bot_quote_currency || '').toUpperCase();

  let portfolioPct: number | null = null;
  if (typeof row?.net_profit_pct === 'number') portfolioPct = row.net_profit_pct;
  else if (typeof row?.total_profit_pct === 'number') portfolioPct = row.total_profit_pct;

  let pairsProfitSum = 0;
  if (pairs.length > 0) {
    pairsProfitSum = pairs.reduce((sum, p) => sum + toNum(p?.profitUsd ?? p?.profit_usd ?? p?.profitAmount ?? p?.profit_amount ?? p?.total_profit ?? p?.net_profit, 0), 0);
  }

  let netProfitUsd = 0;
  if (typeof row?.net_profit_amount === 'number' && row.net_profit_amount !== 0) {
    netProfitUsd = row.net_profit_amount;
  } else if (typeof row?.total_profit_amount === 'number' && row.total_profit_amount !== 0) {
    netProfitUsd = row.total_profit_amount;
  } else if (pairs.length > 0 && pairsProfitSum !== 0) {
    netProfitUsd = pairsProfitSum;
  } else {
    const grossFromDb = toNum(row?.profit, 0) || toNum(row?.pnl_usd, 0) || toNum(row?.total_profit_usd, 0);
    const feesFromDb = toNum(row?.fees_total_usd, 0);
    netProfitUsd = grossFromDb - feesFromDb;
  }

  const calculateFromTrades = (trades: any[]) => {
    let cost = 0; let volume = 0; let fees = 0; let profit = 0;
    trades.forEach((trade: any) => {
      const qty = toNum(trade?.qty || trade?.amount || trade?.quantity, 0);
      const price = toNum(trade?.price || trade?.entryPrice || trade?.entry_price || trade?.currentPrice, 0);
      const positionUsd = toNum(trade?.position_usd || trade?.positionUsd, qty * price);

      if (positionUsd > 0) {
        cost += positionUsd;
        const tradeProfit = toNum(trade?.net_profit || trade?.profit_amount || trade?.profit || trade?.pnl, 0);
        volume += (positionUsd * 2) + Math.abs(tradeProfit); 
        fees += toNum(trade?.fee || trade?.fees, 0);
        profit += tradeProfit;
      }
    });
    return { cost, volume, fees, profit };
  };

  let totalCostUsd = toNum(row?.total_cost_usd, 0);
  let totalVolumeUsd = toNum(row?.total_volume_usd, 0);
  let feesUsd = toNum(row?.fees_total_usd, 0);

  const needsRecalc = totalCostUsd === 0 || totalVolumeUsd === 0;
  if (needsRecalc && Array.isArray(row?.trade_history) && row!.trade_history!.length > 0) {
    const calculated = calculateFromTrades(row!.trade_history!);
    totalCostUsd = calculated.cost;
    totalVolumeUsd = calculated.volume;
    feesUsd = calculated.fees;
    if (netProfitUsd === 0) netProfitUsd = calculated.profit; 
  }

  const roePct = capital > 0 ? (netProfitUsd / capital) * 100 : 0;
  let finalPct = portfolioPct;
  if (pairs.length > 0 || netProfitUsd !== 0 || finalPct === null) finalPct = roePct;

  const turnover = totalCostUsd > 0 ? (totalVolumeUsd / totalCostUsd) : 0;

  const toNumSafe = (v: any): number => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'string') {
      const parsed = parseFloat(v);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    return 0;
  };

  let maxDrawdownPct = toNumSafe((row as any)?.max_drawdown_pct) || toNumSafe((row as any)?.maxDrawdownPct);
  if (maxDrawdownPct === 0) {
    const fallbackVal = toNumSafe((row as any)?.max_drawdown);
    if (fallbackVal > 0 && fallbackVal < 100) maxDrawdownPct = fallbackVal;
  }

  let maxDrawdownUsd = 0;
  const drawdownVal = toNumSafe((row as any)?.max_drawdown);
  if (drawdownVal >= 100) maxDrawdownUsd = drawdownVal;

  return {
    pairsCount,
    pctLabel: 'Profit %',
    usdLabel: 'Profit',
    profitPctText: formatPctFromFraction(finalPct),
    profitUsdText: formatCurrency(netProfitUsd, quote),
    roeLabel: 'ROE %',
    roeText: formatPctFromFraction(roePct),
    maxDrawdownLabel: 'Max Drawdown',
    maxDrawdownText: maxDrawdownUsd > 0 ? `${formatPctFromFraction(maxDrawdownPct)} / ${formatCurrency(maxDrawdownUsd, quote)}` : formatPctFromFraction(maxDrawdownPct),
    maxDrawdownUsd,
    maxDrawdownPct,
    totalCostLabel: 'Cost',
    totalCostText: formatCurrency(totalCostUsd, quote),
    turnoverLabel: 'Turnover',
    turnoverText: turnover > 0 ? `${turnover.toFixed(2)}×` : '—',
    feesLabel: 'Fees',
    feesText: formatCurrency(feesUsd, quote),
    grossProfitUsd: netProfitUsd,
    netProfitUsd,
    feesUsd
  };
}